import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import { listPosts } from "./blogApi";
import { BlogListItem } from "./types";

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export default function BlogList() {
  const colors = tokens(useTheme().palette.mode);
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listPosts()
      .then((data) => setPosts(data.items))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <CircularProgress sx={{ color: colors.primary[500] }} />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: colors.primary[800], minHeight: "100vh", py: 6 }}>
      <Container maxWidth="lg">
        <Typography variant="h2" sx={{ fontWeight: 800, color: colors.primary[700], mb: 1 }}>
          Blog
        </Typography>
        <Typography sx={{ color: colors.primary[300], mb: 4 }}>
          Stories, insights and news from Muzukuru.
        </Typography>

        {posts.length === 0 ? (
          <Typography sx={{ color: colors.primary[300], py: 8, textAlign: "center" }}>
            No posts have been published yet. Check back soon.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
            }}
          >
            {posts.map((post) => (
              <Card
                key={post.id}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  backgroundColor: colors.primary[900],
                  overflow: "hidden",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                }}
              >
                <CardActionArea onClick={() => navigate(`/blog/${post.meta.slug}`)}>
                  {post.featured_image_thumb && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={post.featured_image_thumb.url}
                      alt={post.featured_image_thumb.alt || post.title}
                    />
                  )}
                  <CardContent>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <Chip
                        label={post.content_kind}
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          backgroundColor: colors.primary[600],
                          color: colors.primary[400],
                          fontWeight: 600,
                        }}
                      />
                      {post.category_api && (
                        <Chip
                          label={post.category_api.name}
                          size="small"
                          variant="outlined"
                          sx={{ color: colors.primary[300] }}
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: colors.primary[700], mb: 1 }}
                    >
                      {post.title}
                    </Typography>
                    {post.intro && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.primary[300],
                          mb: 2,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {post.intro}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: colors.primary[300] }}>
                      {post.author_name ? `${post.author_name} · ` : ""}
                      {formatDate(post.publish_date)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}
